import {
  BEHAVIOUR_FILTER_LABEL,
  EXPECTED_FILTER_LABEL,
  KIND_FILTER_LABEL,
  NPC_BEHAVIOUR_FILTER_OPTIONS,
  NPC_KIND_FILTER_OPTIONS,
  type NpcFilters,
  npcBehaviourFilterSchema,
  npcExpectedFilterSchema,
  npcKindFilterSchema,
} from '@/lib/npc-filters';
import { NPC_WORDING } from '@/lib/npc-wording';
import { Select } from '@pms-digital/design-system';

/**
 * The NPC's three own filters, in the place of the two referent selects of the visible row
 * (`FilterBar.renderReferents`; npc.md §12): « Sorte », « Ce qu'il éprouve », « Noyau attendu ».
 *
 * They sit in the row and not behind « Plus de filtres » like a persona's: the maquette draws
 * them there, because an NPC is picked out of fifteen by exactly these three questions - who is
 * it, what does it put to the test, what is it expected to give - and cited by nothing, so the
 * referent filters have nothing to ask.
 */

export interface NpcFilterFieldsProps {
  filters: NpcFilters;
  onChange: (patch: Partial<NpcFilters>) => void;
}

export function NpcFilterFields({ filters, onChange }: NpcFilterFieldsProps) {
  // The test ids sit on wrappers: the package `Select` takes none (its trigger is the
  // `combobox` inside). Every neutral choice is the word `NPC_FILTER_ALL`, never `''`.
  return (
    <>
      <div data-testid={`${NPC_WORDING.entity}-filtre-sorte`} className="w-45 max-w-full">
        <Select
          snug
          label="Sorte"
          value={filters.kind}
          onValueChange={(value) => {
            const kind = npcKindFilterSchema.safeParse(value);
            if (kind.success) onChange({ kind: kind.data });
          }}
          options={NPC_KIND_FILTER_OPTIONS.map((value) => ({
            value,
            label: KIND_FILTER_LABEL[value],
          }))}
        />
      </div>

      <div data-testid={`${NPC_WORDING.entity}-filtre-eprouve`} className="w-52.5 max-w-full">
        <Select
          snug
          label="Ce qu’il éprouve"
          value={filters.behaviour}
          onValueChange={(value) => {
            const behaviour = npcBehaviourFilterSchema.safeParse(value);
            if (behaviour.success) onChange({ behaviour: behaviour.data });
          }}
          options={NPC_BEHAVIOUR_FILTER_OPTIONS.map((value) => ({
            value,
            label: BEHAVIOUR_FILTER_LABEL[value],
          }))}
        />
      </div>

      <div data-testid={`${NPC_WORDING.entity}-filtre-noyau`} className="w-40 max-w-full">
        <Select
          snug
          label="Noyau attendu"
          value={filters.expected}
          onValueChange={(value) => {
            const expected = npcExpectedFilterSchema.safeParse(value);
            if (expected.success) onChange({ expected: expected.data });
          }}
          options={npcExpectedFilterSchema.options.map((value) => ({
            value,
            label: EXPECTED_FILTER_LABEL[value],
          }))}
        />
      </div>
    </>
  );
}
